/**
 * DC «Граф» — визуальная карта связей всей аудитории (force-directed, как граф
 * Obsidian, но нагляднее). Узлы — все, кого мы знаем: установившие приложение,
 * все игроки сессий (тиммейты) и все добавления в Telegram-канал.
 *
 * Категории (узел может быть в нескольких — тогда он раскрашен сегментами):
 *   • curator    (золотой)  — куратор: 5+ броней, приводит команды;
 *   • teammate   (бирюза)   — играл в сессиях (тиммейт);
 *   • subscriber (фиолет)   — добавлен в наш Telegram-канал.
 * Рёбра — кто кого добавил в игру / куратор→тиммейты / реферал на канал.
 *
 * Своя физика на canvas (репульсия + пружины + гравитация), zoom/pan/перетаскивание,
 * клик по узлу → инфо + подсветка соседей. Источник — /api/audience/graph.
 */
import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from "react";
import { api } from "../../api/client";
import css from "./dcSections.module.css";

type Category = "curator" | "booker" | "teammate" | "subscriber" | "operator" | "recruit";
interface GNode { tg: number; name: string; username: string; games: number; booked: number; invited: number; balance: number; cats: Category[]; staff: boolean; dual: boolean }
interface GEdge { from: number; to: number; kind: "team" | "channel" | "operator" }
interface SimNode extends GNode { x: number; y: number; vx: number; vy: number; fx: number; fy: number; r: number; fixed: boolean }

const COLORS: Record<Category, string> = { curator: "#ffce45", booker: "#e0b840", teammate: "#37d6c6", subscriber: "#9b8cf0", operator: "#ff7a5c", recruit: "#ff3ea5" };
const CAT_LABEL: Record<Category, string> = { curator: "Кураторы", booker: "Бронировали", teammate: "Тиммейты", subscriber: "В канале", operator: "Операторы", recruit: "Привёл оператор (QR)" };
const CATS: Category[] = ["curator", "booker", "teammate", "subscriber", "operator", "recruit"];
const legBtn: CSSProperties = { padding: "4px 9px", borderRadius: 7, border: "1px solid var(--surface-3)", background: "var(--surface-2)", color: "var(--text-2)", fontSize: 11, cursor: "pointer" };
const legDot = (c: string): CSSProperties => ({ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: c, verticalAlign: "middle", marginRight: 3 });

type UiState = { names: "none" | "all" | "selected" | "neighbors"; team: boolean; operator: boolean; channel: boolean; intensity: number };
const DEFAULT_UI: UiState = { names: "neighbors", team: true, operator: true, channel: true, intensity: 1 };
const panelBox: CSSProperties = { fontSize: 11, lineHeight: 1.5, color: "var(--text-2)", margin: "0 0 6px", padding: "8px 10px", border: "1px solid var(--surface-3)", borderRadius: 8, background: "var(--surface-1)" };
const ctrlRow: CSSProperties = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 6 };
const ctrlLbl: CSSProperties = { fontSize: 11, color: "var(--text-3)", minWidth: 92 };
const chip = (on: boolean, color?: string): CSSProperties => ({ padding: "3px 8px", borderRadius: 999, fontSize: 11, cursor: "pointer", border: "1px solid " + (on ? (color ?? "var(--role)") : "var(--surface-3)"), background: on ? (color ?? "var(--role)") : "transparent", color: on ? "#06121a" : "var(--text-2)" });

const REP = 2200, SPRING_K = 0.025, SPRING_LEN = 80, GRAVITY = 0.016, DAMP = 0.85, MAXV = 14;

export function DcGraphSection() {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<GNode | null>(null);
  const [counts, setCounts] = useState({ curator: 0, booker: 0, teammate: 0, subscriber: 0, operator: 0, recruit: 0, staff: 0, dual: 0, mixed: 0, edges: 0, total: 0 });
  const [legendOpen, setLegendOpen] = useState(false);
  const [ui, setUi] = useState<UiState>(DEFAULT_UI);

  const G = useRef({
    nodes: [] as SimNode[], edges: [] as GEdge[],
    byId: new Map<number, SimNode>(), adj: new Map<number, Set<number>>(),
    scale: 1, ox: 0, oy: 0, alpha: 1, selTg: null as number | null, raf: 0,
    drag: null as SimNode | null, panning: false, lastX: 0, lastY: 0, moved: 0, downTg: null as number | null,
    pointers: new Map<number, { x: number; y: number }>(), pinch: null as { dist: number; scale: number } | null,
    ui: { ...DEFAULT_UI } as UiState,
  });

  useEffect(() => {
    let live = true;
    api<{ nodes: GNode[]; edges: GEdge[]; total?: number }>("/api/audience/graph")
      .then((d) => {
        if (!live) return;
        initSim(d.nodes, d.edges);
        setCounts({
          curator: d.nodes.filter((n) => n.cats.includes("curator")).length,
          booker: d.nodes.filter((n) => n.cats.includes("booker")).length,
          teammate: d.nodes.filter((n) => n.cats.includes("teammate")).length,
          subscriber: d.nodes.filter((n) => n.cats.includes("subscriber")).length,
          operator: d.nodes.filter((n) => n.cats.includes("operator")).length,
          recruit: d.nodes.filter((n) => n.cats.includes("recruit")).length,
          staff: d.nodes.filter((n) => n.staff).length,
          dual: d.nodes.filter((n) => n.dual).length,
          mixed: d.nodes.filter((n) => n.cats.length > 1).length,
          edges: d.edges.length, total: d.total ?? d.nodes.length,
        });
        setLoading(false);
        start();
      })
      .catch((e: unknown) => { if (live) { setError(e instanceof Error ? e.message : "Граф недоступен"); setLoading(false); } });
    return () => { live = false; cancelAnimationFrame(G.current.raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { G.current.ui = ui; rebuildAdj(); }, [ui]);

  function rebuildAdj() {
    const g = G.current;
    const adj = new Map<number, Set<number>>();
    for (const e of g.edges) {
      if (!g.ui[e.kind]) continue;
      if (!adj.has(e.from)) adj.set(e.from, new Set());
      if (!adj.has(e.to)) adj.set(e.to, new Set());
      adj.get(e.from)!.add(e.to); adj.get(e.to)!.add(e.from);
    }
    g.adj = adj;
  }

  function initSim(nodes: GNode[], edges: GEdge[]) {
    const g = G.current;
    const n = nodes.length || 1;
    const spread = 80 + 30 * Math.sqrt(n);
    g.nodes = nodes.map((nd, i) => {
      const a = i * 2.399963;
      const rad = spread * Math.sqrt((i + 0.5) / n);
      return { ...nd, x: Math.cos(a) * rad, y: Math.sin(a) * rad, vx: 0, vy: 0, fx: 0, fy: 0,
        r: 4 + Math.sqrt(nd.booked * 4 + nd.games * 1.6 + nd.invited * 1.2), fixed: false };
    });
    g.edges = edges;
    g.byId = new Map<number, SimNode>(g.nodes.map((nd) => [nd.tg, nd] as [number, SimNode]));
    rebuildAdj();
    g.scale = 1; g.ox = 0; g.oy = 0; g.alpha = 1;
  }

  function tick() {
    const ns = G.current.nodes, g = G.current;
    for (const nd of ns) { nd.fx = -nd.x * GRAVITY; nd.fy = -nd.y * GRAVITY; }
    for (let i = 0; i < ns.length; i++) {
      const a = ns[i]!;
      for (let j = i + 1; j < ns.length; j++) {
        const b = ns[j]!;
        let dx = a.x - b.x, dy = a.y - b.y, d2 = dx * dx + dy * dy;
        if (d2 < 0.01) { dx = Math.random() - 0.5; dy = Math.random() - 0.5; d2 = 0.01; }
        const dist = Math.sqrt(d2), f = REP / d2, ux = dx / dist, uy = dy / dist;
        a.fx += f * ux; a.fy += f * uy; b.fx -= f * ux; b.fy -= f * uy;
      }
    }
    for (const e of g.edges) {
      const a = g.byId.get(e.from), b = g.byId.get(e.to);
      if (!a || !b) continue;
      const dx = b.x - a.x, dy = b.y - a.y, dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const diff = (dist - SPRING_LEN) * SPRING_K, ux = dx / dist, uy = dy / dist;
      a.fx += diff * ux; a.fy += diff * uy; b.fx -= diff * ux; b.fy -= diff * uy;
    }
    for (const nd of ns) {
      if (nd.fixed) { nd.vx = 0; nd.vy = 0; continue; }
      nd.vx = Math.max(-MAXV, Math.min(MAXV, (nd.vx + nd.fx) * DAMP));
      nd.vy = Math.max(-MAXV, Math.min(MAXV, (nd.vy + nd.fy) * DAMP));
      nd.x += nd.vx; nd.y += nd.vy;
    }
    g.alpha *= 0.992;
  }

  function draw() {
    const g = G.current, canvas = canvasRef.current, wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = wrap.clientWidth, h = wrap.clientHeight;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) { canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr); }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const cx = w / 2, cy = h / 2;
    const SX = (x: number) => cx + g.ox + x * g.scale, SY = (y: number) => cy + g.oy + y * g.scale;
    const sel = g.selTg, neigh = sel != null ? g.adj.get(sel) : undefined;

    const inten = g.ui.intensity;
    for (const e of g.edges) {
      if (!g.ui[e.kind]) continue;
      const a = g.byId.get(e.from), b = g.byId.get(e.to);
      if (!a || !b) continue;
      const active = sel != null && (e.from === sel || e.to === sel);
      const rgb = e.kind === "operator" ? "255,122,92" : e.kind === "channel" ? "155,140,240" : "150,150,170";
      const baseA = active ? 0.65 : e.kind === "operator" ? 0.20 : e.kind === "channel" ? 0.14 : 0.12;
      ctx.strokeStyle = active ? `rgba(120,230,210,${Math.min(0.95, 0.65 * inten)})` : `rgba(${rgb},${Math.min(0.95, baseA * inten)})`;
      ctx.lineWidth = active ? 1.6 : 1;
      ctx.beginPath(); ctx.moveTo(SX(a.x), SY(a.y)); ctx.lineTo(SX(b.x), SY(b.y)); ctx.stroke();
    }

    const trace = (x: number, y: number, rr: number, star: boolean) => {
      ctx.beginPath();
      if (star) {
        for (let i = 0; i < 10; i++) {
          const ang = -Math.PI / 2 + (i * Math.PI) / 5;
          const k = i % 2 === 0 ? rr * 1.35 : rr * 0.56;
          const px = x + Math.cos(ang) * k, py = y + Math.sin(ang) * k;
          if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.closePath();
      } else {
        ctx.arc(x, y, rr, 0, Math.PI * 2);
      }
    };
    for (const nd of g.nodes) {
      const r = Math.max(2.5, nd.r * g.scale), x = SX(nd.x), y = SY(nd.y);
      const dim = sel != null && nd.tg !== sel && !(neigh && neigh.has(nd.tg));
      ctx.globalAlpha = dim ? 0.25 : 1;
      const cats = nd.cats.length ? nd.cats : (["subscriber"] as Category[]);
      const star = nd.staff;
      if (!dim && cats.includes("curator")) {
        ctx.shadowColor = COLORS.curator; ctx.shadowBlur = 10;
        trace(x, y, r, star); ctx.fillStyle = COLORS.curator; ctx.fill();
        ctx.shadowBlur = 0;
      }
      ctx.save(); trace(x, y, r, star); ctx.clip();
      if (cats.includes("recruit")) {
        // Приведён личным QR оператора — точка целиком фуксия (ключевой сигнал «оператор привёл клиента»).
        ctx.fillStyle = COLORS.recruit; ctx.fillRect(x - r * 1.7, y - r * 1.7, r * 3.4, r * 3.4);
      } else if (cats.length <= 1) {
        ctx.fillStyle = COLORS[cats[0]!]; ctx.fillRect(x - r * 1.7, y - r * 1.7, r * 3.4, r * 3.4);
      } else {
        const step = (Math.PI * 2) / cats.length;
        for (let i = 0; i < cats.length; i++) {
          ctx.beginPath(); ctx.moveTo(x, y);
          ctx.arc(x, y, r * 1.8, -Math.PI / 2 + i * step, -Math.PI / 2 + (i + 1) * step);
          ctx.closePath(); ctx.fillStyle = COLORS[cats[i]!]; ctx.fill();
        }
      }
      ctx.restore();
      if (star && !dim) { trace(x, y, r, true); ctx.lineWidth = 1.3; ctx.strokeStyle = "rgba(255,255,255,0.78)"; ctx.stroke(); }
      if (nd.tg === sel) { trace(x, y, r + 1.5, star); ctx.lineWidth = 2; ctx.strokeStyle = "#fff"; ctx.stroke(); }
      ctx.globalAlpha = 1;
      const nm = g.ui.names;
      const showLabel = !dim && nm !== "none" && (nm === "all" || (nm === "selected" && nd.tg === sel) || (nm === "neighbors" && (nd.tg === sel || (neigh != null && neigh.has(nd.tg)))));
      if (showLabel && nd.name) {
        ctx.font = "10px system-ui, sans-serif"; ctx.fillStyle = "rgba(230,234,240,0.85)"; ctx.textAlign = "center";
        ctx.fillText(nd.name.length > 14 ? nd.name.slice(0, 13) + "…" : nd.name, x, y - (star ? r * 1.35 : r) - 3);
      }
    }
  }

  function start() {
    const g = G.current;
    cancelAnimationFrame(g.raf);
    const loop = () => { if (g.alpha > 0.02 || g.drag) tick(); draw(); g.raf = requestAnimationFrame(loop); };
    g.raf = requestAnimationFrame(loop);
  }
  const reheat = () => { G.current.alpha = 0.9; };

  function worldAt(clientX: number, clientY: number) {
    const g = G.current, wrap = wrapRef.current!;
    const rect = wrap.getBoundingClientRect();
    const sx = clientX - rect.left, sy = clientY - rect.top;
    const cx = wrap.clientWidth / 2, cy = wrap.clientHeight / 2;
    return { x: (sx - cx - g.ox) / g.scale, y: (sy - cy - g.oy) / g.scale, sx, sy };
  }
  function pick(clientX: number, clientY: number): SimNode | null {
    const g = G.current, p = worldAt(clientX, clientY);
    let best: SimNode | null = null, bestD = Infinity;
    for (const nd of g.nodes) {
      const dx = nd.x - p.x, dy = nd.y - p.y, d = Math.sqrt(dx * dx + dy * dy);
      if (d < nd.r + 6 / g.scale && d < bestD) { bestD = d; best = nd; }
    }
    return best;
  }
  const onDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    const g = G.current;
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (g.pointers.size >= 2) {
      if (g.drag) g.drag.fixed = false;
      g.drag = null; g.panning = false;
      const pts = [...g.pointers.values()];
      const p0 = pts[0]!, p1 = pts[1]!;
      g.pinch = { dist: Math.hypot(p0.x - p1.x, p0.y - p1.y) || 1, scale: g.scale };
      return;
    }
    g.moved = 0; g.lastX = e.clientX; g.lastY = e.clientY;
    const hit = pick(e.clientX, e.clientY);
    g.downTg = hit ? hit.tg : null;
    if (hit) { g.drag = hit; hit.fixed = true; reheat(); } else g.panning = true;
  };
  const onMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const g = G.current;
    if (g.pointers.has(e.pointerId)) g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (g.pointers.size >= 2 && g.pinch) {
      const pts = [...g.pointers.values()];
      const p0 = pts[0]!, p1 = pts[1]!;
      const dist = Math.hypot(p0.x - p1.x, p0.y - p1.y) || 1;
      const ns = Math.max(0.2, Math.min(5, g.pinch.scale * (dist / g.pinch.dist)));
      const wrap = wrapRef.current!; const rect = wrap.getBoundingClientRect();
      const mx = (p0.x + p1.x) / 2 - rect.left, my = (p0.y + p1.y) / 2 - rect.top;
      const cx = wrap.clientWidth / 2, cy = wrap.clientHeight / 2;
      const wx = (mx - cx - g.ox) / g.scale, wy = (my - cy - g.oy) / g.scale;
      g.scale = ns; g.ox = mx - cx - wx * ns; g.oy = my - cy - wy * ns;
      return;
    }
    if (!g.drag && !g.panning) return;
    const dx = e.clientX - g.lastX, dy = e.clientY - g.lastY;
    g.lastX = e.clientX; g.lastY = e.clientY; g.moved += Math.abs(dx) + Math.abs(dy);
    if (g.drag) { const p = worldAt(e.clientX, e.clientY); g.drag.x = p.x; g.drag.y = p.y; g.drag.vx = 0; g.drag.vy = 0; }
    else { g.ox += dx; g.oy += dy; }
  };
  const onUp = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const g = G.current;
    g.pointers.delete(e.pointerId);
    if (g.pointers.size < 2) g.pinch = null;
    if (g.pinch) return;
    if (g.drag) g.drag.fixed = false;
    if (g.moved < 6 && g.downTg != null) {
      const nd = g.byId.get(g.downTg) || null;
      g.selTg = g.selTg === g.downTg ? null : g.downTg;
      setSelected(g.selTg ? (nd as GNode) : null);
    } else if (g.moved < 6 && g.downTg == null && g.pointers.size === 0) { g.selTg = null; setSelected(null); }
    g.drag = null; g.panning = false; g.downTg = null;
    if (g.pointers.size === 1) { const p = [...g.pointers.values()][0]!; g.lastX = p.x; g.lastY = p.y; g.moved = 99; }
  };
  const onWheel = (e: ReactWheelEvent<HTMLCanvasElement>) => {
    const g = G.current, p = worldAt(e.clientX, e.clientY);
    const ns = Math.max(0.25, Math.min(4, g.scale * (e.deltaY < 0 ? 1.12 : 1 / 1.12)));
    const cx = wrapRef.current!.clientWidth / 2, cy = wrapRef.current!.clientHeight / 2;
    g.ox = p.sx - cx - p.x * ns; g.oy = p.sy - cy - p.y * ns; g.scale = ns;
  };
  const fit = () => { const g = G.current; g.scale = 1; g.ox = 0; g.oy = 0; reheat(); };

  if (error) return <div className={css.main}><div className={css.err}>Ошибка загрузки графа: {error}</div></div>;
  if (loading) return <div className={css.main}><div className={css.loading}>Строим граф…</div></div>;

  return (
    <div style={{ padding: "10px 12px calc(var(--safe-bottom, 0px) + 12px)" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginBottom: 6 }}>
        {CATS.map((c) => (
          <span key={c} title={CAT_LABEL[c]} style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, color: "var(--text-2)" }}>
            <i style={{ width: 9, height: 9, borderRadius: "50%", background: COLORS[c], display: "inline-block" }} />
            <b style={{ color: "var(--text-1)" }}>{counts[c]}</b>
          </span>
        ))}
        <span style={{ fontSize: 11, color: "var(--text-3)" }}>★ <b style={{ color: "var(--text-1)" }}>{counts.staff}</b></span>
        <span style={{ fontSize: 11, color: "var(--text-3)" }}>связей <b style={{ color: "var(--text-1)" }}>{counts.edges}</b></span>
        <button type="button" onClick={() => setLegendOpen((o) => !o)} style={legBtn}>{legendOpen ? "скрыть" : "настройки"}</button>
        <button type="button" onClick={fit} style={{ ...legBtn, marginLeft: "auto" }}>Сбросить</button>
      </div>
      {legendOpen ? (
        <div style={panelBox}>
          <div style={ctrlRow}>
            <span style={ctrlLbl}>Имена</span>
            {([["none", "Нет"], ["all", "Все"], ["selected", "Выбранный"], ["neighbors", "+ связи"]] as const).map(([v, l]) => (
              <button key={v} type="button" style={chip(ui.names === v)} onClick={() => setUi((s) => ({ ...s, names: v }))}>{l}</button>
            ))}
          </div>
          <div style={ctrlRow}>
            <span style={ctrlLbl}>Связи</span>
            <button type="button" style={chip(ui.team, COLORS.teammate)} onClick={() => setUi((s) => ({ ...s, team: !s.team }))}>Тиммейты</button>
            <button type="button" style={chip(ui.operator, COLORS.operator)} onClick={() => setUi((s) => ({ ...s, operator: !s.operator }))}>Операторы</button>
            <button type="button" style={chip(ui.channel, COLORS.subscriber)} onClick={() => setUi((s) => ({ ...s, channel: !s.channel }))}>Канал</button>
          </div>
          <div style={ctrlRow}>
            <span style={ctrlLbl}>Яркость линий</span>
            <input type="range" min={0.2} max={2} step={0.1} value={ui.intensity} onChange={(e) => setUi((s) => ({ ...s, intensity: Number(e.target.value) }))} style={{ flex: 1, minWidth: 120, accentColor: "var(--role)" }} />
          </div>
          <div style={{ borderTop: "1px solid var(--surface-3)", marginTop: 4, paddingTop: 6 }}>
            <div><i style={legDot(COLORS.curator)} /><b>Кураторы</b> — 5+ броней: приводят команды, всё организуют (ярче + свечение).</div>
            <div><i style={legDot(COLORS.booker)} /><b>Бронировали</b> — есть 1–4 брони.</div>
            <div><i style={legDot(COLORS.teammate)} /><b>Тиммейты</b> — играли в командах.</div>
            <div><i style={legDot(COLORS.subscriber)} /><b>В канале</b> — добавлены в наш Telegram-канал.</div>
            <div><i style={legDot(COLORS.operator)} /><b>Операторы</b> — сотрудники, кто добавлял людей.</div>
            <div>★ <b>звезда</b> — сотрудник Юниквеста (есть и в стафф-аппе).</div>
            <div>Несколько цветов в узле — несколько ролей сразу.</div>
          </div>
        </div>
      ) : null}

      <div ref={wrapRef} style={{ position: "relative", height: "calc(100dvh - 150px)", minHeight: 360, borderRadius: 12, overflow: "hidden", border: "1px solid var(--surface-3)", background: "radial-gradient(120% 90% at 50% 0%, rgba(120,230,210,0.05), transparent 60%), var(--surface-0, #0b0f12)", touchAction: "none", cursor: "grab" }}>
        <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }}
          onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onWheel={onWheel} />
        {counts.total === 0 ? <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "var(--text-3)", fontSize: 13 }}>Пока никого нет в базе</div> : null}

        {selected ? (
          <div style={{ position: "absolute", left: 10, right: 10, bottom: 10, padding: "10px 12px", borderRadius: 10, background: "color-mix(in srgb, var(--surface-1) 92%, transparent)", border: "1px solid var(--surface-3)", backdropFilter: "blur(4px)" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span style={{ display: "inline-flex", gap: 2 }}>
                {selected.cats.map((c) => <i key={c} style={{ width: 8, height: 8, borderRadius: "50%", background: COLORS[c], display: "inline-block" }} />)}
              </span>
              <strong style={{ fontSize: 14, color: "var(--text-1)" }}>{selected.name || "Без имени"}</strong>
              {selected.username ? <span style={{ fontSize: 11, color: "var(--text-3)" }}>@{selected.username}</span> : null}
              <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-3)" }}>{selected.cats.map((c) => CAT_LABEL[c]).join(" + ")}</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 6, fontSize: 12, color: "var(--text-2)" }}>
              <span>🪙 <b style={{ color: "var(--text-1)" }}>{selected.balance}</b></span>
              <span>🎮 <b style={{ color: "var(--text-1)" }}>{selected.games}</b></span>
              <span>🛒 <b style={{ color: "var(--text-1)" }}>{selected.booked}</b></span>
              <span>🔗 привёл <b style={{ color: "var(--text-1)" }}>{selected.invited}</b></span>
            </div>
          </div>
        ) : (
          <div style={{ position: "absolute", left: 10, bottom: 10, fontSize: 11, color: "var(--text-3)" }}>Тяни узлы · два пальца / колесо — масштаб · клик — связи</div>
        )}
      </div>
    </div>
  );
}
